import { useMemo } from 'react';
import { useThemeColors } from '@/shared/theme/useThemeColors';
import { typography, semantic, radius } from '@/shared/theme/tokens';
import { Sparkline } from '../../ui/Sparkline';
import { LIVE_STATUS_META, computeStatus } from './consensusMeta';

const r1 = (n) => Math.round(n * 10) / 10;

// Up to 2 headline metrics for the compact handoff panel, preferring target-type.
function pickMetrics(alerts, max = 2) {
  const byName = new Map();
  for (const a of alerts) {
    if (!byName.has(a.parameterName)) byName.set(a.parameterName, a);
  }
  const all = [...byName.values()];
  const targets = all.filter((a) => a.paramType === 'target');
  return [...targets, ...all.filter((a) => a.paramType !== 'target')].slice(0, max);
}

// Fraction of all buffered samples that sat inside the optimal band — a session-
// derived "how steady was the plant" number. Honest: it's from the live buffers,
// not a real 30-day aggregate, hence the explicit "Last 12h" framing.
function inBandPct(alerts) {
  let total = 0;
  let inBand = 0;
  for (const a of alerts) {
    for (const v of a.buffer ?? []) {
      total += 1;
      if (computeStatus(v, a) === 'ok') inBand += 1;
    }
  }
  return total ? Math.round((inBand / total) * 100) : null;
}

/**
 * Shift-handoff summary — "what changed / how did the plant sit this window".
 * Surfaces handoff *memory*, not a reporting dashboard. Window is the rolling
 * "Last 12h" (no real shift-roster data in the prototype, so we don't call it
 * a "shift"). All figures are session-derived from the live buffers + the
 * current alert/rec snapshot.
 */
export function PerformanceStats({ alerts = [], recs = [] }) {
  const c = useThemeColors();
  const metrics = useMemo(() => pickMetrics(alerts), [alerts]);

  const { pct, activeAlerts, pending, awaiting } = useMemo(() => ({
    pct: inBandPct(alerts),
    activeAlerts: alerts.filter((a) => !a.isStale && a.status !== 'ok').length,
    pending: recs.filter((r) => r.status === 'unacknowledged').length,
    awaiting: recs.filter((r) => r.status === 'accepted').length,
  }), [alerts, recs]);

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1,
      borderRadius: radius['2xl'], border: `1px solid ${c.border}`, background: c.bg, overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
        borderBottom: `1px solid ${c.border}`, flexShrink: 0,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18, color: semantic.successAlt }}>insights</span>
        <span style={{ fontFamily: typography.fontFamily.heading, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>
          Session Summary
        </span>
        <span style={{ marginLeft: 'auto', fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide }}>
          Last 12h
        </span>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', minHeight: 0 }}>
        {/* Handoff stat row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
          <Stat label="In band" value={pct == null ? '—' : `${pct}%`} color={semantic.successAlt} c={c} />
          <Stat label="Active alerts" value={activeAlerts} color={activeAlerts ? semantic.error : c.textPrimary} c={c} />
          <Stat label="Awaiting" value={awaiting} color={awaiting ? '#0891b2' : c.textPrimary} c={c} sub="confirmation" />
        </div>
        <div style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>
          {pending} recommendation{pending === 1 ? '' : 's'} pending a decision · session-derived
        </div>

        {/* Session-derived metric tiles */}
        <div style={{ display: 'grid', gridTemplateColumns: metrics.length > 1 ? '1fr 1fr' : '1fr', gap: 10 }}>
          {metrics.map((m) => {
            const peak = m.buffer.length ? r1(Math.max(...m.buffer)) : m.liveValue;
            const avg = m.buffer.length ? r1(m.buffer.reduce((s, v) => s + v, 0) / m.buffer.length) : m.liveValue;
            const meta = LIVE_STATUS_META[m.status] ?? LIVE_STATUS_META.ok;
            return (
              <div key={m.id} style={{
                display: 'flex', flexDirection: 'column', gap: 4,
                padding: '10px 12px', borderRadius: radius.lg,
                background: c.bgSubtle, border: `1px solid ${c.borderSubtle}`,
              }}>
                <span style={{
                  fontSize: typography.fontSize.xs, color: c.textSubtle,
                  textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {m.parameterName}
                </span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                  <span style={{ fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontVariantNumeric: 'tabular-nums' }}>
                    {peak}
                  </span>
                  {m.unit && <span style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{m.unit}</span>}
                  <span style={{ fontSize: typography.fontSize.xs, color: meta.color, marginLeft: 2 }}>peak</span>
                </div>
                <Sparkline data={m.buffer} color={meta.color} height={26} animate={false} />
                <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, fontVariantNumeric: 'tabular-nums' }}>
                  session avg {avg}{m.unit ? ` ${m.unit}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color, sub, c }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1,
      padding: '8px 10px', borderRadius: radius.lg, background: c.bgSubtle, border: `1px solid ${c.borderSubtle}`,
    }}>
      <span style={{ fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {value}
      </span>
      <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle, textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide, lineHeight: 1.2 }}>
        {label}{sub ? ` ${sub}` : ''}
      </span>
    </div>
  );
}
